import {
  Component,
  ComponentFactoryResolver,
  ComponentRef,
  HostBinding,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewContainerRef,
  ChangeDetectionStrategy,
} from "@angular/core";
import { Subscription } from "rxjs";
import moment from "moment";
import { DualCommunicatorService } from "../../services/internal-services/dual-communicator.service";
import { PhoenixViewerContainerComponent } from "../../components/containers/phoenix-viewer-container/phoenix-viewer-container.component";
import { ViewerConfigurationService } from "../../services/viewer-configuration.service";
import { IViewerconfiguration } from "../../interfaces/i-viewerconfiguration";
import { ViewerConstants } from "../../../shared/model/viewer-constants";
import { AgentDefinition } from "../../../ai-components/chat/model/agent.types";
import type { ToolRendererConfig } from "../../../ai-components/chat/model/tool-data.types";

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: "lib-phoenix-viewer",
  templateUrl: "./phoenix-viewer-page.component.html",
  styleUrls: ["./phoenix-viewer-page.component.scss"],
  standalone: false,
})
export class LibCollectionViewerComponent implements OnInit, OnDestroy {
  @HostBinding("class.dual-container")
  dualView = false;

  @ViewChild("container", { read: ViewContainerRef, static: true })
  container: ViewContainerRef;

  componentRef: ComponentRef<PhoenixViewerContainerComponent>;

  mapCounter = 0;

  subscriptions: Subscription[] = [];

  constructor(
    private dualService: DualCommunicatorService,
    private componentFactoryResolver: ComponentFactoryResolver,
    private viewerConfigurationService: ViewerConfigurationService,
  ) {}

  private _enableDualView = false;

  /** AI agents forwarded to all map containers. */
  @Input() aiAgents: AgentDefinition[] = [];

  /** Tool renderers forwarded to all map containers. */
  @Input() aiToolRenderers: ToolRendererConfig[] = [];

  @Input()
  set enableDualView(enabled: boolean) {
    this.dualView = enabled;
    if (enabled !== this._enableDualView) {
      this._enableDualView = enabled;
      if (enabled) {
        this.addMapComponent();
      } else {
        this.removeMapComponent();
      }
    }
  }

  ngOnInit() {
    this.subscriptions.push(
      this.dualService.dualEnabled$.subscribe((enabled: boolean) => {
        this.enableDualView = enabled;
      }),
    );

    this.subscriptions.push(
      this.viewerConfigurationService.viewerConfiguration$.subscribe((viewerConf) => {
        if (viewerConf) {
          this.initializeComponent(viewerConf);
        }
      }),
    );
  }

  ngOnDestroy() {
    this.subscriptions.forEach((sub: Subscription) => sub.unsubscribe());
    this.componentRef.destroy();
    this.container.clear();
  }

  /**
   * Remove all views in container and add one
   */
  initializeComponent(viewerConf: IViewerconfiguration) {
    this.dualService.updateDualStatus(false);
    this.mapCounter = 0;
    this.dualView = false;
    this.container.clear();
    this.addMapComponent(viewerConf);
  }

  addMapComponent(viewerConf: IViewerconfiguration = null) {
    this.mapCounter += 1;
    const componentFactory = this.componentFactoryResolver.resolveComponentFactory(PhoenixViewerContainerComponent);
    this.componentRef = this.container.createComponent(componentFactory);
    this.componentRef.instance.mapIndex = this.mapCounter;

    const limitDay = moment().add(ViewerConstants.WARNING_EXPIRE_DAYS, "days");
    this.componentRef.instance.warningExpireUser = viewerConf?.userExpireDate
      ? limitDay.diff(moment(viewerConf.userExpireDate)) >= 0
      : false;

    this.componentRef.instance.warningExpireInstance = viewerConf?.instanceExpireDate
      ? limitDay.diff(moment(viewerConf.instanceExpireDate)) >= 0
      : false;

    this.componentRef.instance.aiAgents = this.aiAgents;
    this.componentRef.instance.aiToolRenderers = this.aiToolRenderers;
  }

  removeMapComponent() {
    if (this.mapCounter > 1) {
      this.mapCounter -= 1;
      this.container.remove(this.mapCounter);
    }
  }
}
